import { useEffect, useState } from 'react'
import type { Media } from '@nhanho/shared'
import { media as api, explainError, type MediaMetaBody } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { useList } from '../../lib/useList'
import { useDebounced } from '../../lib/useDebounced'
import { useToast } from '../../lib/toast'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input, Textarea } from '../../components/ui/Input'
import { Field } from '../../components/ui/Field'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { Spinner } from '../../components/ui/Spinner'
import { UploadDropzone, altProblem } from '../../components/media/UploadDropzone'

const fmtBytes = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`)

export default function MediaLibrary() {
  const { data, loading, refresh, params, set } = useList<Media>((p) => api.list({ ...p, limit: 48 }))
  const toast = useToast()
  const [detail, setDetail] = useState<Media | null>(null)
  const [alt, setAlt] = useState('')
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [view, setView] = useState<'grid' | 'table'>('grid')
  /**
   * Ô đã sửa nhưng chưa lưu, khoá theo id ảnh.
   *
   * **Cố ý giữ qua các trang.** Xoá khi đổi trang (như `ProductList` làm với ô chọn) là
   * đúng cho *lựa chọn* nhưng sai cho *dữ liệu đã gõ* — người ta mất công viết rồi mất
   * trắng mà không có gì báo. Số ô đang sửa hiện ngay trên thanh lưu để không ai quên.
   */
  const [edits, setEdits] = useState<Record<string, MediaMetaBody>>({})
  const [busy, setBusy] = useState(false)
  /**
   * `DELETE /api/media/:id` là `requireAdmin` — ảnh dùng chung nhiều nơi. Sửa alt/tên thì
   * `manager` làm được (`PATCH /:id` + `POST /bulk`), nên chỉ giấu đúng nút xoá.
   */
  const { isAdmin } = useAuth()
  const [del, setDel] = useState<Media | null>(null)
  const [inUse, setInUse] = useState<string[] | null>(null)
  const [altError, setAltError] = useState<string | null>(null)
  const [q, setQ] = useState(String(params.search ?? ''))
  const dq = useDebounced(q, 300)
  useEffect(() => { if (dq !== String(params.search ?? '')) set({ search: dq }) }, [dq]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mở ảnh khác thì phải xoá cả lỗi alt và danh sách "đang được dùng" của ảnh trước.
  const openDetail = (m: Media) => {
    setDetail(m); setAlt(m.alt); setTitle(m.title ?? ''); setDesc(m.description ?? '')
    setAltError(null); setInUse(null)
  }
  const saveDetail = async () => {
    if (!detail) return
    const problem = altProblem(alt)
    if (problem) return setAltError(problem)
    setBusy(true); setAltError(null)
    try {
      await api.update(detail.id, { alt: alt.trim(), title: title.trim(), description: desc.trim() })
      toast.success('Đã lưu thông tin ảnh'); setDetail(null); refresh()
    }
    catch (e) {
      const { message, fieldErrors } = explainError(e)
      setAltError(fieldErrors.alt ?? message)
    } finally { setBusy(false) }
  }
  const remove = async () => {
    if (!del) return
    setBusy(true); setInUse(null)
    try { await api.remove(del.id); toast.success('Đã xoá ảnh khỏi Garage'); setDel(null); setDetail(null); refresh() }
    catch (e) {
      const info = explainError(e)
      if (info.status === 409) {
        const d = (e as { details?: unknown }).details as
          { products?: { id: string; title: string }[]; posts?: { id: string; title: string }[]; categories?: { id: string; title: string }[] } | undefined
        setInUse([...(d?.products ?? []), ...(d?.posts ?? []), ...(d?.categories ?? [])].map((x) => x.title))
      } else toast.error(explainError(e).message)
    } finally { setBusy(false) }
  }
  const copy = (url: string) => navigator.clipboard.writeText(url).then(() => toast.info('Đã copy URL'))

  // ─── Sửa hàng loạt trong chế độ bảng ───────────────────────────────────────
  const dirtyCount = Object.keys(edits).length
  /** Giá trị đang hiện trong ô: bản đang sửa nếu có, không thì giá trị trong DB. */
  const cell = (m: Media, field: keyof MediaMetaBody) => edits[m.id]?.[field] ?? (m[field] ?? '')
  const editCell = (m: Media, field: keyof MediaMetaBody, value: string) =>
    setEdits((prev) => {
      const next = { ...prev, [m.id]: { ...prev[m.id], [field]: value } }
      // Sửa rồi sửa về đúng giá trị cũ = không còn là thay đổi. Không dọn thì thanh lưu
      // báo "3 ảnh đã sửa" trong khi thực tế không có gì khác.
      const row = next[m.id]!
      const unchanged = (Object.keys(row) as (keyof MediaMetaBody)[]).every((k) => (row[k] ?? '') === (m[k] ?? ''))
      if (unchanged) delete next[m.id]
      return next
    })

  const saveAll = async () => {
    const items = Object.entries(edits).map(([id, fields]) => ({ id, ...fields }))
    if (!items.length) return
    const blank = items.find((it) => it.alt !== undefined && !it.alt.trim())
    if (blank) return toast.error('Alt text không được để trống')
    setBusy(true)
    try {
      const r = await api.bulk(items)
      toast.success(`Đã lưu ${r.affected} ảnh`)
      setEdits({}); refresh()
    } catch (e) { toast.error(explainError(e).message) } finally { setBusy(false) }
  }

  return (
    <>
      <PageHeader title="Hình ảnh" subtitle={data ? `${data.totalDocs} ảnh trên Garage` : undefined} />
      <div className="mb-4 grid gap-3 md:grid-cols-[1fr_360px]">
        {/*
          `items-start`: ô của grid mặc định kéo giãn hết chiều cao hàng, mà hàng này cao
          bằng khung kéo-thả bên phải — không có nó thì ô tìm kiếm cao gấp ba lần bình thường.
        */}
        <div className="flex items-start gap-2">
          <Input placeholder="Tìm theo alt hoặc tên file…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Button variant="secondary" className="shrink-0 whitespace-nowrap" onClick={() => setView((v) => (v === 'grid' ? 'table' : 'grid'))}>
            {view === 'grid' ? 'Sửa hàng loạt' : 'Xem lưới'}
          </Button>
        </div>
        <UploadDropzone compact onUploaded={refresh} />
      </div>

      {/*
        Thanh lưu hiện ngay khi có ô bị sửa. Đếm theo **ảnh**, không theo ô: người ta nghĩ
        theo "đã sửa 3 tấm", không theo "đã sửa 7 ô".
      */}
      {dirtyCount > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-button bg-primary-100 px-3 py-2 text-sm">
          <span className="font-medium text-ink">{dirtyCount} ảnh đã sửa, chưa lưu</span>
          <Button size="sm" loading={busy} onClick={saveAll}>Lưu tất cả</Button>
          <Button size="sm" variant="ghost" onClick={() => setEdits({})}>Bỏ thay đổi</Button>
        </div>
      )}
      {loading && !data ? <Spinner /> : (
        <>
          {view === 'grid' ? (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {data?.docs.map((m) => (
                <button key={m.id} type="button" onClick={() => openDetail(m)} className="group relative aspect-square overflow-hidden rounded-image border border-border bg-sand hover:border-primary-300" title={m.alt}>
                  <img src={m.sizes?.thumbnail?.url ?? m.url} alt={m.alt} className="size-full object-cover" loading="lazy" />
                  <span className="absolute inset-x-0 bottom-0 truncate bg-ink/60 px-1.5 py-0.5 text-left text-2xs text-white opacity-0 group-hover:opacity-100">{m.alt}</span>
                </button>
              ))}
            </div>
          ) : (
            /*
              Chế độ bảng — sửa alt/tiêu đề/mô tả tại chỗ cho nhiều ảnh rồi lưu một lần.
              Đây là lý do tính năng này tồn tại: trước đó muốn đổi alt phải mở từng ảnh,
              gõ, bấm lưu, đóng — nhân với số ảnh.
            */
            <div className="overflow-x-auto rounded-card-sm border border-border">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-sand text-left text-xs uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="w-16 px-3 py-2">Ảnh</th>
                    <th className="w-48 px-3 py-2">Tên file</th>
                    <th className="px-3 py-2">Alt text <span className="text-danger">*</span></th>
                    <th className="px-3 py-2">Tiêu đề</th>
                    <th className="px-3 py-2">Mô tả</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data?.docs.map((m) => (
                    <tr key={m.id} className={edits[m.id] ? 'bg-primary-50' : undefined}>
                      <td className="px-3 py-2">
                        <button type="button" onClick={() => openDetail(m)} className="block size-11 overflow-hidden rounded-image border border-border bg-sand" title="Mở chi tiết">
                          <img src={m.sizes?.thumbnail?.url ?? m.url} alt={m.alt} className="size-full object-cover" loading="lazy" />
                        </button>
                      </td>
                      <td className="max-w-48 truncate px-3 py-2 font-mono text-2xs text-ink-muted" title={m.filename}>{m.filename}</td>
                      <td className="px-3 py-2">
                        <Input value={String(cell(m, 'alt'))} aria-label={`Alt text của ${m.filename}`} onChange={(e) => editCell(m, 'alt', e.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <Input value={String(cell(m, 'title'))} maxLength={300} aria-label={`Tiêu đề của ${m.filename}`} onChange={(e) => editCell(m, 'title', e.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <Input value={String(cell(m, 'description'))} maxLength={1000} aria-label={`Mô tả của ${m.filename}`} onChange={(e) => editCell(m, 'description', e.target.value)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data && !data.docs.length && <p className="py-12 text-center text-sm text-ink-muted">Chưa có ảnh nào.</p>}
          {data && <div className="mt-4"><Pagination data={data} onPage={(p) => set({ page: p })} /></div>}
        </>
      )}

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Chi tiết ảnh" wide
        footer={<>{isAdmin && <Button variant="danger" onClick={() => detail && setDel(detail)}>Xoá ảnh</Button>}<Button variant="secondary" onClick={() => setDetail(null)}>Đóng</Button><Button onClick={saveDetail} loading={busy}>Lưu</Button></>}>
        {detail && (
          <div className="grid gap-5 md:grid-cols-[1fr_320px]">
            <div className="overflow-hidden rounded-image bg-sand"><img src={detail.sizes?.card?.url ?? detail.url} alt={detail.alt} className="max-h-[60vh] w-full object-contain" /></div>
            <div className="space-y-4 text-sm">
              <Field label="Alt text" required hint="Mô tả ảnh cho SEO + người dùng đọc màn hình" error={altError ?? undefined}><Input value={alt} aria-invalid={!!altError} onChange={(e) => { setAlt(e.target.value); setAltError(null) }} /></Field>
              <Field label="Tiêu đề ảnh" hint="Tuỳ chọn. Hiện khi rê chuột, và là tên Google Images hay dùng."><Input value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} /></Field>
              <Field label="Mô tả ảnh" hint="Tuỳ chọn. Dành cho sitemap ảnh."><Textarea value={desc} rows={2} maxLength={1000} onChange={(e) => setDesc(e.target.value)} /></Field>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs text-ink-muted">
                <dt>File</dt><dd className="truncate text-ink">{detail.filename}</dd>
                <dt>Kích thước</dt><dd className="text-ink">{detail.width}×{detail.height} · {fmtBytes(detail.filesize)}</dd>
                <dt>Loại</dt><dd className="text-ink">{detail.mimeType}</dd>
              </dl>
              <div>
                <div className="mb-1.5 text-xs font-medium text-ink">Các biến thể</div>
                <ul className="space-y-1 text-xs">
                  <li className="flex items-center justify-between gap-2"><span>Gốc · {detail.width}×{detail.height}</span><Button variant="ghost" size="sm" onClick={() => copy(detail.url)}>Copy URL</Button></li>
                  {(['thumbnail', 'card', 'hero'] as const).map((k) => {
                    const v = detail.sizes?.[k]
                    return v ? <li key={k} className="flex items-center justify-between gap-2"><span>{k} · {v.width}×{v.height} · {fmtBytes(v.filesize)}</span><Button variant="ghost" size="sm" onClick={() => copy(v.url)}>Copy URL</Button></li>
                      : <li key={k} className="text-ink-faint">{k} — không sinh (ảnh gốc nhỏ hơn)</li>
                  })}
                </ul>
              </div>
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog open={!!del} onCancel={() => { setDel(null); setInUse(null) }} onConfirm={remove} loading={busy} title="Xoá ảnh"
        message={inUse ? (
          <div><p className="text-danger">Ảnh đang được dùng, không xoá được. Gỡ ảnh khỏi các mục sau trước:</p><ul className="mt-2 list-disc pl-5">{inUse.map((t) => <li key={t}>{t}</li>)}</ul></div>
        ) : <>Xoá <strong>{del?.alt}</strong> và toàn bộ biến thể khỏi Garage? Không khôi phục được.</>} />
    </>
  )
}
